<template>
  <v-container class="container-form">
    <div v-if="fetchingData" class="text-center pa-10">
      <v-progress-circular
        color="primary"
        indeterminate
        :size="70"
        :width="7"
      ></v-progress-circular>
      <h4 class="mt-4">Cargando datos del médico...</h4>
    </div>

    <v-card
      v-else-if="errorUser && !fetchingData"
      class="pa-5 mb-5 border-error"
      elevation="2"
    >
      <v-row align="center" no-gutters>
        <v-col cols="4" class="text-center">
          <v-img
            src="@/assets/img/logo.png"
            alt="Logo Aliquam"
            max-width="80"
            class="mx-auto"
          ></v-img>
        </v-col>
        <v-col cols="8" class="pl-4">
          <div class="text-h6 mb-2">Usuario no encontrado</div>
          <p class="text-body-2 mb-4">
            No pudimos localizar los datos del médico asociado a este RFC.
          </p>
          <v-btn
            color="primary"
            block
            @click="reloadPage"
            prepend-icon="mdi-refresh"
          >
            Reintentar cargar
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <v-form v-else ref="form" id="crTramite" @submit.prevent="checkCaptcha">
      <div class="text-center">
        <v-img
          src="@/assets/img/logo.png"
          alt="Logo Aliquam"
          max-width="150"
          class="mx-auto"
        ></v-img>
        <h4>Bienvenido</h4>
        <h3 v-if="apiPopulated">{{ user[0]?.name }}</h3>
      </div>

      <h5 style="display: flex; margin: 2em 2em 2em 0">
        Ingrese los datos del paciente para la Solicitud de su Carta de
        Autorización
      </h5>

      <v-text-field
        v-model="formData.x_patient_name"
        label="Nombre(s) *"
        :rules="[(v) => !!v || 'Este campo es obligatorio']"
        required
        variant="outlined"
      ></v-text-field>

      <v-text-field
        v-model="formData.x_patient_first_name"
        label="Apellido paterno *"
        :rules="[(v) => !!v || 'Este campo es obligatorio']"
        required
        variant="outlined"
      ></v-text-field>

      <v-text-field
        v-model="formData.x_patient_last_name"
        label="Apellido materno *"
        :rules="[(v) => !!v || 'Este campo es obligatorio']"
        required
        variant="outlined"
      ></v-text-field>

      <v-text-field
        v-model="formData.x_patient_birth_date"
        label="Fecha de nacimiento"
        type="date"
        variant="outlined"
      ></v-text-field>

      <v-text-field
        v-model="formData.x_attention_start_date"
        label="Fecha de ingreso *"
        type="date"
        :rules="[(v) => !!v || 'Este campo es obligatorio']"
        required
        variant="outlined"
      ></v-text-field>

      <v-text-field
        v-model="formData.x_attention_end_date"
        label="Fecha de egreso"
        type="date"
        variant="outlined"
      ></v-text-field>

      <v-text-field
        v-model="formData.x_folio_number"
        label="Folio Hospital"
        variant="outlined"
      ></v-text-field>

      <v-select
        v-model="formData.place_of_procedure"
        :items="places"
        item-title="name"
        item-value="id"
        label="Seleccionar lugar de procedimiento *"
        variant="outlined"
        :rules="[(v) => !!v || 'Este campo es obligatorio']"
        required
        return-object
      ></v-select>

      <v-select
        v-model="formData.medic_participation"
        :items="participations"
        item-title="name"
        item-value="id"
        label="Seleccionar participación *"
        variant="outlined"
        :rules="[(v) => !!v || 'Este campo es obligatorio']"
        required
        return-object
      ></v-select>

      <v-autocomplete
        v-model="formData.insurer_id"
        :items="sortedPartners"
        item-title="name"
        item-value="id"
        label="Seleccionar aseguradora *"
        variant="outlined"
        :rules="[(v) => !!v || 'Este campo es obligatorio']"
        required
        return-object
        v-model:search="search"
      ></v-autocomplete>

      <v-textarea
        v-model="formData.description"
        label="Observaciones"
        variant="outlined"
      ></v-textarea>

      <div class="loader-container" v-if="loading">
        <v-progress-circular
          color="primary"
          indeterminate
          :size="45"
          :width="10"
        ></v-progress-circular>
      </div>

      <v-btn
        type="submit"
        class="mb-3 mt-3 submit-button kiosko-btn"
        :disabled="loading"
      >
        Enviar solicitud
      </v-btn>
    </v-form>
  </v-container>
</template>

<script setup>
import { ref, onMounted, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { fetchData, createLead } from "@/router/services/apiService";

const route = useRoute();
const router = useRouter();

const user = ref([]);
const errorUser = ref(false);
const apiPopulated = ref(false);
const loading = ref(false); // Para el envío del formulario
const fetchingData = ref(false); // Para la carga inicial del médico
const search = ref("");

const formData = ref({
  partner_id: "",
  vat: "",
  x_patient_name: "",
  x_patient_first_name: "",
  x_patient_last_name: "",
  x_attention_start_date: "",
  place_of_procedure: null,
  medic_participation: null,
  insurer_id: null,
  description: "",
});

const places = ref([]);
const participations = ref([]);
const partners = ref([]);

const sortedPartners = computed(() => {
  return partners.value.slice().sort((a, b) => {
    const nameA = a.name.toUpperCase();
    const nameB = b.name.toUpperCase();
    return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
  });
});

const fetchFormData = async () => {
  fetchingData.value = true; // Iniciamos carga de datos del médico
  errorUser.value = false;
  apiPopulated.value = false;

  try {
    const data = await fetchData(route.params.rfc);

    if (data && data.user && data.user.length > 0) {
      user.value = [...data.user];
      formData.value.partner_id = user.value[0].id;
      formData.value.vat = user.value[0].vat;
      apiPopulated.value = true;
    } else {
      errorUser.value = true;
    }

    if (data.places) places.value = [...data.places];
    if (data.partners) partners.value = [...data.partners];
    if (data.participations) participations.value = [...data.participations];
  } catch (error) {
    console.error("Error fetching data: ", error);
    errorUser.value = true;
  } finally {
    fetchingData.value = false; // Finalizamos carga de datos
  }
};

const reloadPage = () => {
  window.location.reload();
};

const checkCaptcha = async () => {
  const captchaToken = true;
  if (captchaToken) {
    await submitFormData(captchaToken);
  }
};

const submitFormData = async (captchaToken) => {
  loading.value = true;

  // Normalización de datos a mayúsculas antes de enviar
  const submissionData = {
    ...formData.value,
    x_patient_name: formData.value.x_patient_name.toUpperCase(),
    x_patient_first_name: formData.value.x_patient_first_name.toUpperCase(),
    x_patient_last_name: formData.value.x_patient_last_name.toUpperCase(),
    captchaToken,
  };

  const requiredFields = {
    x_patient_name: "Nombre del paciente",
    x_patient_first_name: "Apellido paterno",
    x_patient_last_name: "Apellido materno",
    x_attention_start_date: "Fecha de Ingreso",
    place_of_procedure: "Lugar de procedimiento",
    medic_participation: "Participación del médico",
  };

  let missingFields = [];
  for (const field in requiredFields) {
    if (!formData.value[field]) {
      missingFields.push(requiredFields[field]);
    }
  }

  if (missingFields.length > 0) {
    alert(
      `Por favor, complete los siguientes campos obligatorios: ${missingFields.join(
        ", ",
      )}`,
    );
    loading.value = false;
    return;
  }

  try {
    const response = await createLead(submissionData);
    const rfc_value = user.value[0].vat;

    if (response.success) {
      router.push({
        name: "onSuccess",
        query: { isSuccess: true, rfc: rfc_value },
        params: { rfc: rfc_value },
      });
    } else {
      alert(response.message || "Error al procesar la solicitud.");
    }
  } catch (error) {
    console.error("Error submitting form:", error);
    alert("Ocurrió un error inesperado al enviar la solicitud.");
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchFormData();
});

watch(
  () => route.params.rfc,
  (newRfc, oldRfc) => {
    if (newRfc !== oldRfc) {
      fetchFormData();
    }
  },
);
</script>

<style>
.container-form {
  max-width: 600px;
  margin: 0 auto;
  padding: 20px;
}

.border-error {
  border-left: 5px solid #ff5252 !important;
}

.loader-container {
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
}

.kiosko-btn {
  width: 100%;
  font-weight: bold;
}
</style>
